import { EXTRACTION_ROW_FRAME_KIND } from './DocumentExtractionFieldsTab.constants';

export const aiFieldCardStyles = {
  border: ({ palette }) => `1px solid ${palette.divider}`,
  borderRadius: '8px',
  padding: '10px 12px',
  marginBottom: '8px',
  backgroundColor: ({ palette }) => palette.background.paper,
};

export const emptyFieldsBoxStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  py: 3,
};

export const sectionHeaderStyles = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '12px',
};

export const headerActionsStyles = {
  marginLeft: 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
};

export const editAllButtonStyles = {
  padding: 0,
  minWidth: 0,
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  color: 'primary.main',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
};

// Filter-chip toggle that reorders the review list to surface below-threshold
// fields first. Filled when active, outlined when idle.
export const getSortToggleButtonStyles = ({ isActive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 10px',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  cursor: 'pointer',
  color: isActive ? 'primary.contrastText' : 'primary.main',
  border: ({ palette }) => `1px solid ${palette.primary.light}`,
  backgroundColor: ({ palette }) => (isActive ? palette.primary.main : palette.background.paper),
});

export const footerStyles = {
  display: 'flex',
  gap: '8px',
  padding: '14px 16px',
  borderTop: ({ palette }) => `1px solid ${palette.divider}`,
  backgroundColor: ({ palette }) => palette.background.default,
  flexShrink: 0,
};

export const editFormWrapperStyles = {
  flex: 1,
  overflowY: 'auto',
  padding: '0 16px',
};

// Amber-tinted card for a low-confidence result awaiting review (mockup 5).
// Dark mode keeps the border via warning.main and uses the low-alpha soft fill.
export const aiFieldCardNeedsReviewStyles = {
  ...aiFieldCardStyles,
  border: ({ palette }) =>
    `1px solid ${palette.mode === 'dark' ? palette.warning.dark : palette.warning.light}`,
  backgroundColor: ({ palette }) =>
    palette.mode === 'dark' ? palette.warning.soft : palette.warning.extraSoft,
};

export const emptyFieldValueStyles = {
  marginTop: '2px',
};

export const skeletonCardStyles = {
  ...aiFieldCardStyles,
  padding: '12px',
};

export const reviewActionsRowStyles = {
  display: 'flex',
  gap: '6px',
  marginTop: '8px',
};

export const correctButtonStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 10px',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  color: 'primary.main',
  border: ({ palette }) => `1px solid ${palette.primary.light}`,
  backgroundColor: ({ palette }) => palette.background.paper,
};

export const notFoundAccordionStyles = {
  marginTop: '4px',
  border: ({ palette }) => `1px solid ${palette.divider}`,
  borderRadius: '8px',
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '& .MuiAccordionSummary-root': {
    minHeight: 0,
    padding: '10px 12px',
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
  },
  '& .MuiAccordionDetails-root': {
    padding: '0 12px 10px',
    borderTop: ({ palette }) => `1px solid ${palette.divider}`,
  },
};

export const miniFieldRowStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '6px 0',
  borderBottom: ({ palette }) => `1px dashed ${palette.divider}`,
  '&:last-child': { borderBottom: 'none' },
};

export const footerButtonWrapStyles = {
  flex: 1,
  display: 'flex',
};

// Keep the footer action labels on one line — at the inspector's fixed ~320px width each
// button gets ~140px, and the longest label ("Re-run extraction") otherwise wraps to a second
// line that overflows the button height. nowrap lets each button grow to its label width and
// share the row evenly instead.
export const footerButtonStyles = {
  whiteSpace: 'nowrap',
};

export const virtualizedListStyles = {
  '& .ReactVirtualized__Grid': {
    outline: 'none',
  },
};

export const getVirtualizedRowStyles = (isNested, isBoxOpening) => ({
  display: 'flow-root',
  ...(isNested && { '& .my-12': { marginTop: isBoxOpening ? '12px' : 0 } }),
});

export const getFrameSegmentStyles = (kind, isFirst, isLast) => {
  const isCard = kind === EXTRACTION_ROW_FRAME_KIND.CARD;
  const radius = isCard ? '8px' : '5px';

  return {
    position: 'relative',
    display: 'flow-root',
    paddingLeft: isCard ? '12px' : '10px',
    paddingRight: isCard ? '12px' : '10px',
    borderLeft: ({ palette }) => `1px solid ${palette.divider}`,
    borderRight: ({ palette }) => `1px solid ${palette.divider}`,
    ...(isCard && { backgroundColor: ({ palette }) => palette.background.paper }),
    ...(isFirst && {
      marginTop: isCard ? 0 : '12px',
      paddingTop: '10px',
      borderTop: ({ palette }) => `1px solid ${palette.divider}`,
      borderTopLeftRadius: radius,
      borderTopRightRadius: radius,
    }),
    ...(isLast && {
      marginBottom: isCard ? '8px' : '12px',
      paddingBottom: '10px',
      borderBottom: ({ palette }) => `1px solid ${palette.divider}`,
      borderBottomLeftRadius: radius,
      borderBottomRightRadius: radius,
    }),
  };
};

export const frameLabelStyles = {
  position: 'absolute',
  top: '-8px',
  left: '8px',
  background: ({ palette }) => palette.background.paper,
  padding: '0 5px',
  maxWidth: 'calc(100% - 18px)',
};
